import { NikoInsuranceManagementEditForm } from 'application/niko-insurance-management/forms';
import { BaseEditViewStore, BaasicDropdownStore } from 'core/stores';
import { applicationContext } from 'core/utils';
import { LookupService } from "common/services";
import { InsuranceService } from 'application/import-reference/services';
import { action, observable } from 'mobx';

@applicationContext
class NikoInsuranceManagementEditViewStore extends BaseEditViewStore {
    @observable accessor isSyncing;

    constructor(rootStore) {
        const id = rootStore.routerStore.routerState.params.id;
        const service = new InsuranceService(rootStore.application.baasic.apiClient);

        const lookupService = new LookupService(
            rootStore.application.baasic.apiClient
        );

        super(rootStore, {
            name: 'niko-insurance-management-edit',
            id: id,
            Form: NikoInsuranceManagementEditForm,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async (id, opts) => {
                        const response = await service.get(id, opts);
                        return response.data;
                    },
                    update: async (resource) => {
                        const response = await service.update(resource);
                        return response.data;
                    },
                    create: async (resource) => {
                        const response = await service.create({ ...resource, primarySort: 1000, secondarySort: 1000 });
                        return response.data;
                    }
                }
            },
            FormClass: NikoInsuranceManagementEditForm
        });

        this.pricingGroupStore = new BaasicDropdownStore(
            {
                filterable: true,
            },
            {
                fetchFunc: async (term) => {
                    const response = await lookupService.getLookups("pricingGroup");
                
                    return response["pricingGroup"]
                        .map(item => ({
                            id: item.id,
                            abrv: item.abrv,
                            name: item.name
                        }))
                        .filter(item => {
                            if (!term || term.trim() === "") return true;
                
                            return item.name.toLowerCase().includes(term.toLowerCase());
                        })
                        .sort((a, b) => a.name.localeCompare(b.name));
                },                
            }
        );

        this.placeOfServiceStore = new BaasicDropdownStore(
            {
                filterable: true
            },
            {
                fetchFunc: async (term) => {
                    const response = await lookupService.getLookups("placeOfService");

                    return response["placeOfService"]
                        .map(item => ({
                            id: item.id,
                            abrv: item.abrv,
                            name: item.name
                        }))
                        .filter(item => {
                            if (!term || term.trim() === "") return true;

                            return item.name.toLowerCase().includes(term.toLowerCase());
                        })
                        .sort((a, b) => a.name.localeCompare(b.name));
                },
            }
        );

        this.branchOfficeStore = new BaasicDropdownStore(
            {
                filterable: true
            },
            {
                fetchFunc: async (term) => {
                    const response = await lookupService.getLookups("branchOffice");

                    return response["branchOffice"]
                        .map(item => ({
                            id: item.id,
                            abrv: item.abrv,
                            name: item.name
                        }))
                        .filter(item => {
                            if (!term || term.trim() === "") return true;

                            return item.name.toLowerCase().includes(term.toLowerCase());
                        })
                        .sort((a, b) => a.name.localeCompare(b.name));
                },
            }
        );

        // Static 1/2 choice per ISG-1935. No fetchFunc, so the items are taken
        // straight from the initial state and never refetched.
        this.drainageBagAllowableStore = new BaasicDropdownStore(
            {},
            {},
            [
                { id: 1, name: '1' },
                { id: 2, name: '2' }
            ]
        );
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;
        const selectedId = this.form.$("pricingGroupId").value;
        if (!selectedId) {
            this.form.$("pricingGroupId").validate();
            this.form.$("pricingGroupId").showErrors(true);
            return;
        }
        const exists = this.pricingGroupStore.items.some(
            item => item.id === selectedId
        );
        if (!exists) {
            this.form.$("pricingGroupId").set(null);
            this.form.$("pricingGroupId").validate();
            this.form.$("pricingGroupId").showErrors(true);
            return;
        }
        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.update({
                id: this.id,
                ...resource
            });

            this.form.setFieldsDisabled(false);

            if (this.shouldGoBackAfterAction){
                await this.rootStore.routerStore.goBack();
            }
            await setTimeout(() => this.notifySuccessUpdate(this.name), 10);
        }
        catch(err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    @action.bound
    async syncNikoPriceGroups() {
        this.isSyncing = true;
        const lookupService = new LookupService(
            this.rootStore.application.baasic.apiClient
        );
        await lookupService.syncNikoPriceGroups();
        await this.pricingGroupStore.filterAsync();
        this.isSyncing = false;
    }

    @action.bound
    notifySuccessCreate() {
        this.rootStore.notificationStore.success('NIKO_INSURANCE_MANAGEMENT.CREATE.SUCCESS_CREATE');
    }

    @action.bound
    notifySuccessUpdate(name) {
        this.rootStore.notificationStore.success('NIKO_INSURANCE_MANAGEMENT.EDIT.SUCCESS_UPDATE', name);
    }

    @action.bound
    onUpdateError(error) {
        if (!this.errorActions.onUpdateError) {
            const message =
                error && error.data ? error.data :
                error && error.message ? error.message :
                "An error occurred";
            this.rootStore.notificationStore.error(message);
            return;
        }
        return this.errorActions.onUpdateError(error);
    }    
}

export default NikoInsuranceManagementEditViewStore;
